import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Dashboard() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [services, setServices] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [switchingRole, setSwitchingRole] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, [user?.role]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
        axios.get(`${API}/orders`, { withCredentials: true })
      ]);

      setStats(statsRes.data);
      setOrders(ordersRes.data);

      // If provider, fetch their services
      if (user?.role === 'provider') {
        const servicesRes = await axios.get(`${API}/services/provider/${user.user_id}`);
        setServices(servicesRes.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleSwitch = async (newRole) => {
    try {
      setSwitchingRole(true);
      const response = await axios.post(
        `${API}/auth/set-role`,
        { role: newRole },
        { withCredentials: true }
      );
      updateUser(response.data);
      await fetchDashboardData();
    } catch (error) {
      console.error('Error switching role:', error);
      alert('Failed to switch role. Please try again.');
    } finally {
      setSwitchingRole(false);
    }
  };

  const deleteService = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;

    try {
      await axios.delete(`${API}/services/${serviceId}`, { withCredentials: true });
      await fetchDashboardData();
    } catch (error) {
      console.error('Error deleting service:', error);
      alert('Failed to delete service.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900">Dashboard</h1>
          
          {/* Role Switcher */}
          <div className="flex items-center gap-4" data-testid="role-switcher">
            <span className="text-gray-600">I am a:</span>
            <button
              onClick={() => handleRoleSwitch('buyer')}
              disabled={switchingRole}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                user?.role === 'buyer'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
              data-testid="role-buyer-btn"
            >
              Buyer
            </button>
            <button
              onClick={() => handleRoleSwitch('provider')}
              disabled={switchingRole}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                user?.role === 'provider'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
              data-testid="role-provider-btn"
            >
              Provider
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {user?.role === 'provider' ? (
              <>
                <div className="bg-white p-6 rounded-xl shadow-lg" data-testid="stat-services">
                  <p className="text-gray-600 text-sm mb-1">Total Services</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total_services}</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-lg" data-testid="stat-orders">
                  <p className="text-gray-600 text-sm mb-1">Total Orders</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.completed_orders}/{stats.total_orders}</p>
                  <p className="text-xs text-gray-500">Completed/Total</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-lg" data-testid="stat-earnings">
                  <p className="text-gray-600 text-sm mb-1">Total Earnings</p>
                  <p className="text-3xl font-bold text-green-600">${stats.total_earnings?.toFixed(2) || '0.00'}</p>
                  <p className="text-xs text-gray-500">After 15% platform fee</p>
                </div>
              </>
            ) : (
              <>
                <div className="bg-white p-6 rounded-xl shadow-lg" data-testid="stat-purchases">
                  <p className="text-gray-600 text-sm mb-1">Total Purchases</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total_purchases}</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-lg" data-testid="stat-completed">
                  <p className="text-gray-600 text-sm mb-1">Completed</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.completed_purchases}</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-lg" data-testid="stat-spent">
                  <p className="text-gray-600 text-sm mb-1">Total Spent</p>
                  <p className="text-3xl font-bold text-blue-600">${stats.total_spent?.toFixed(2) || '0.00'}</p>
                </div>
              </>
            )}
          </div>
        )}

        {/* Provider Services */}
        {user?.role === 'provider' && (
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-900">My Services</h2>
              <button
                onClick={() => navigate('/create-service')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                data-testid="create-service-btn"
              >
                + Create Service
              </button>
            </div>

            {services.length === 0 ? (
              <div className="bg-white p-8 rounded-xl shadow-lg text-center" data-testid="no-services">
                <p className="text-gray-600 mb-4">You haven't created any services yet.</p>
                <button
                  onClick={() => navigate('/create-service')}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create Your First Service
                </button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="my-services-grid">
                {services.map(service => (
                  <div
                    key={service.service_id}
                    className="bg-white rounded-xl shadow-lg overflow-hidden"
                    data-testid={`my-service-${service.service_id}`}
                  >
                    {service.image_url ? (
                      <img src={service.image_url} alt={service.title} className="w-full h-40 object-cover" />
                    ) : (
                      <div className="w-full h-40 bg-gradient-to-br from-blue-400 to-purple-500"></div>
                    )}
                    <div className="p-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{service.title}</h3>
                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">{service.description}</p>
                      <div className="flex justify-between items-center">
                        <span className="text-xl font-bold text-blue-600">${service.price}</span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => navigate(`/service/${service.service_id}`)}
                            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                            data-testid={`view-service-${service.service_id}`}
                          >
                            View
                          </button>
                          <button
                            onClick={() => deleteService(service.service_id)}
                            className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
                            data-testid={`delete-service-${service.service_id}`}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Orders */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {user?.role === 'provider' ? 'Received Orders' : 'My Purchases'}
          </h2>

          {orders.length === 0 ? (
            <div className="bg-white p-8 rounded-xl shadow-lg text-center" data-testid="no-orders">
              <p className="text-gray-600">
                {user?.role === 'provider' 
                  ? 'No orders received yet.' 
                  : 'You haven\'t made any purchases yet.'}
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-lg overflow-hidden" data-testid="orders-list">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {orders.map(order => (
                    <tr key={order.order_id} data-testid={`order-${order.order_id}`}>
                      <td className="px-6 py-4 text-sm text-gray-900">{order.order_id}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{order.service_id}</td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                        ${order.amount}
                        {user?.role === 'provider' && (
                          <span className="block text-xs text-green-600">
                            You get: ${order.provider_amount?.toFixed(2)}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          order.status === 'completed' 
                            ? 'bg-green-100 text-green-800'
                            : order.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {new Date(order.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;